import { adminAuthenticatedFetch } from "../../common/session";

async function json(response) {
  if (!response.ok) {
    const err = new Error(`Request failed: ${response.status}`);
    err.response = response;
    throw err;
  }
  return response.json();
}

async function withErrorJson(response) {
  let data;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const err = new Error(data?.error || `Request failed: ${response.status}`);

    Object.assign(err, data); //  attach full API response
    err.status = response.status;

    throw err;
  }

  return data;
}
class AdminService {

  async getCallRecordSubscription() {
    const res = await adminAuthenticatedFetch(
      `/api/callRecordSubscriptionHandler/subscription`,
    );
    return json(res);
  }

  async postCallRecordSubscription(expirationInDays) {
    const res = await adminAuthenticatedFetch(`/api/callRecordSubscriptionHandler/subscription`, {
      method: "POST",
      body: JSON.stringify({ expirationInDays }),
    });
    return json(res);
  }

  async refreshCallRecordSubscription() {
    const res = await adminAuthenticatedFetch(
      `/api/callRecordSubscriptionHandler/refreshSubscription`,
      { method: "POST" },
    );
    return json(res);
  }

  async postAdminOnboarding() {
    const res = await adminAuthenticatedFetch(`/api/db/tenant/onboard`, {
      method: "POST",
    });
    return json(res);
  }

  async postAdminOnboardingSync(syncCreatedAt) {
    const res = await adminAuthenticatedFetch(`/api/db/tenant/onboard/sync`, {
      method: "POST",
      body: JSON.stringify({ syncCreatedAt }),
    });
    return json(res);
  }

  async postAdminOnboardingClient(clientCreatedAt) {
    const res = await adminAuthenticatedFetch(`/api/db/tenant/onboard/client`, {
      method: "POST",
      body: JSON.stringify({ clientCreatedAt }),
    });
    return json(res);
  }

  async getUserInfo() {
    const res = await adminAuthenticatedFetch(`/api/db/user/info`);
    return json(res);
  }

  async getManageCustomers(params = {}) {
    const { page = 1, pageSize = 25, search, sortBy, sortOrder } = params;
    // eslint-disable-next-line no-undef
    const queryParams = new URLSearchParams();
    queryParams.append("page", page);
    queryParams.append("pageSize", pageSize);
    if (search) queryParams.append("search", search);
    if (sortBy) queryParams.append("sortBy", sortBy);
    if (sortOrder) queryParams.append("sortOrder", sortOrder);
    const res = await adminAuthenticatedFetch(`/api/admin/getTenantsInfo?${queryParams.toString()}`);
    return withErrorJson(res);
  }

  async checkManageCustomers() {
    const res = await adminAuthenticatedFetch(`/api/admin/isAuthorizedManagedCustomer`);
    return json(res);
  }

  async getUsersOfTenant(searchUsersTenantId, params = {}) {
    const {
      page = 1,
      pageSize = 25,
      search,
      sortBy,
      sortOrder,
    } = params;

    // eslint-disable-next-line no-undef
    const queryParams = new URLSearchParams();

    queryParams.append("page", page);
    queryParams.append("pageSize", pageSize);

    if (searchUsersTenantId) {
      queryParams.append("searchUsersTenantId", searchUsersTenantId);
    }

    if (search) {
      queryParams.append("search", search);
    }

    if (sortBy) {
      queryParams.append("sortBy", sortBy);
      if (sortOrder) {
        queryParams.append("sortOrder", sortOrder);
      }
    }

    const baseUrl = searchUsersTenantId
      ? "/api/admin/getUsersOfTenant"
      : "/api/admin/getManageUsersOfTenant";

    const url = `${baseUrl}?${queryParams.toString()}`;

    const res = await adminAuthenticatedFetch(url);
    return withErrorJson(res);
  }

  async getSFOrgsOfTenant(searchTenantId) {
    const res = await adminAuthenticatedFetch(`/api/admin/getSFOrgsOfTenant?searchTenantId=${encodeURIComponent(searchTenantId)}`);
    return withErrorJson(res);
  }

  async deleteMsTeamsUser(msUserId, deleteUserTenantId) {
    const res = await adminAuthenticatedFetch(`/api/admin/deleteMsTeamsUser?msUserId=${encodeURIComponent(msUserId)}&deleteUserTenantId=${encodeURIComponent(deleteUserTenantId)}`, {
      method: "DELETE"
    });
    return json(res);
  }

  async msUserDeactivation(msUserId, deactivateMsUserTenantId) {
    const res = await adminAuthenticatedFetch(deactivateMsUserTenantId ? `/api/admin/msUserDeactivation?msUserId=${encodeURIComponent(msUserId)}&deactivateMsUserTenantId=${encodeURIComponent(deactivateMsUserTenantId)}` : `/api/admin/manageUserMsUserDeactivation?msUserId=${encodeURIComponent(msUserId)}`, {
      method: "POST"
    });
    return json(res);
  }

  async msUserActivation(msUserId, activateMsUserTenantId) {
    const res = await adminAuthenticatedFetch(activateMsUserTenantId ? `/api/admin/msUserActivation?msUserId=${encodeURIComponent(msUserId)}&activateMsUserTenantId=${encodeURIComponent(activateMsUserTenantId)}` : `/api/admin/manageUserMsUserActivation?msUserId=${encodeURIComponent(msUserId)}`, {
      method: "POST"
    });
    return json(res);
  }

  async deleteSFOrg(sfOrgId, deleteSfOrgTenantId) {
    const res = await adminAuthenticatedFetch(`/api/admin/deleteSFOrg?sfOrgId=${encodeURIComponent(sfOrgId)}&deleteSfOrgTenantId=${encodeURIComponent(deleteSfOrgTenantId)}`, {
      method: "DELETE"
    });
    return json(res);
  }

  async getSFConnection() {
    const res = await adminAuthenticatedFetch(`/api/db/salesforce/orgs`);
    return json(res);
  }

  async getQueuesData(params = {}) {
    const { page = 1, pageSize = 25, search, sortBy, sortOrder, searchTenantId } = params;
    // eslint-disable-next-line no-undef
    const queryParams = new URLSearchParams();
    queryParams.append("page", page);
    queryParams.append("pageSize", pageSize);
    if (search) queryParams.append("search", search);
    if (sortBy) queryParams.append("sortBy", sortBy);
    if (sortOrder) queryParams.append("sortOrder", sortOrder);
    if (searchTenantId) queryParams.append("searchTenantId", searchTenantId);
    const res = await adminAuthenticatedFetch(`/api/admin/getQueuesData?${queryParams.toString()}`);
    return withErrorJson(res);
  }

  /**
   * Link a Salesforce organization to one or more queues.
   *
   * Normal selection:
   *   linkSalesforceOrg({ tenantSfOrgId, queues: [{ queueId, queueName }] })
   *
   * Select-all across pages:
   *   linkSalesforceOrg({ tenantSfOrgId, selectionMode: "ALL", filters: { search } })
   */
  async linkSalesforceOrg({ tenantSfOrgId, queues, selectionMode, filters, searchTenantId } = {}) {
    const res = await adminAuthenticatedFetch(`/api/admin/queues/link-salesforce-org`, {
      method: "POST",
      body: JSON.stringify({ tenantSfOrgId, queues, selectionMode, filters, searchTenantId }),
    });
    return withErrorJson(res);
  }

  /**
   * Remove the Salesforce organization link from a single queue.
   * @param {string} queueId - The Salesforce Queue ID (queue_mappings.queue_id)
   */
  async unlinkSalesforceOrg(queueId, searchTenantId) {
    const res = await adminAuthenticatedFetch(`/api/admin/queues/unlink-salesforce-org`, {
      method: "DELETE",
      body: JSON.stringify({ queueId, searchTenantId }),
    });
    return withErrorJson(res);
  }

  /**
   * Bulk remove the Salesforce organization link from multiple queues.
   *
   * Normal selection:
   *   bulkUnlinkSalesforceOrg({ queueIds: ["id1", "id2"] })
   *
   * Select-all across pages:
   *   bulkUnlinkSalesforceOrg({ selectionMode: "ALL", filters: { search } })
   */
  async bulkUnlinkSalesforceOrg({ selectionMode, queueIds, filters, searchTenantId } = {}) {
    const res = await adminAuthenticatedFetch(`/api/admin/queues/bulk-unlink-salesforce-org`, {
      method: "DELETE",
      body: JSON.stringify({ selectionMode, queueIds, filters, searchTenantId }),
    });
    return withErrorJson(res);
  }

  /**
   * Trigger a granular sync — fetches all queues from the MS Teams / Salesforce
   * source and upserts them into the database. Returns the refreshed queue
   * list plus a `syncSummary` with fetched/inserted/updated/skipped counts.
   * @param {string} [searchTenantId] - Cross-tenant admin: restrict to this tenant.
   */
  async syncQueues(searchTenantId) {
    const res = await adminAuthenticatedFetch(`/api/admin/queues-sync-all`, {
      method: "POST",
      body: JSON.stringify(searchTenantId ? { searchTenantId } : {}),
    });
    return withErrorJson(res);
  }

  async deleteSFConnection(sfOrgId) {
    const res = await adminAuthenticatedFetch(`/api/db/salesforce/org/${sfOrgId}`, {
      method: "DELETE",
    });
    return json(res);
  }

  /**
   * Generate one-time SCV link credentials for a Salesforce org.
   * Response shape: { username, password, expiresAt }. The plaintext password
   * is only returned once — the dialog must display it immediately.
   */
  async generateScvCredentials(sfOrgId) {
    const res = await adminAuthenticatedFetch(`/api/admin/scv/generateCredentials`, {
      method: "POST",
      body: JSON.stringify({ sfOrgId }),
    });
    return withErrorJson(res);
  }

  async editACSConnection(acsInfo) {
    const res = await adminAuthenticatedFetch(`/api/keyvault/tenant/config`, {
      method: "POST",
      body: JSON.stringify(acsInfo),
    });
    return json(res);
  }

  /**
   * Lazy-fetch the masked ACS connection string for a single tenant.
   * Used by EditAcsDialog on open so the tenants list endpoint doesn't
   * have to resolve every tenant's Key Vault secret up front.
   */
  async getTenantAcs(searchTenantId) {
    const res = await adminAuthenticatedFetch(
      `/api/admin/getTenantAcs?searchTenantId=${encodeURIComponent(searchTenantId)}`,
    );
    return withErrorJson(res);
  }

  /**
   * Fetch Salesforce users belonging to a specific Salesforce org.
   * Supports server-side search, sorting, and pagination.
   *
   * @param {string} sfOrgId           - The Salesforce org ID (e.g. "00DdL00000qXv9dUAC")
   * @param {object} [params]
   * @param {string} [params.search]   - Full-text search term
   * @param {string} [params.sortBy]   - Column to sort by (e.g. "username", "sfUserId")
   * @param {string} [params.sortOrder]- "asc" | "desc"
   * @param {number} [params.page]     - 1-based page number
   * @param {number} [params.pageSize] - Results per page
   */
  async getSFUsersOfOrg(sfOrgId, params = {}) {
    const { search, sortBy, sortOrder, page, pageSize, searchTenantId, pinnedSfUserId } = params;
    const q = new URLSearchParams();
    q.set("sfOrgId", sfOrgId);
    if (search)         q.set("search",         search);
    if (sortBy)         q.set("sortBy",         sortBy);
    if (sortOrder)      q.set("sortOrder",      sortOrder);
    if (page     != null) q.set("page",         page);
    if (pageSize != null) q.set("pageSize",     pageSize);
    if (searchTenantId) q.set("searchTenantId", searchTenantId);
    if (pinnedSfUserId) q.set("pinnedSfUserId", pinnedSfUserId);
    const res = await adminAuthenticatedFetch(
      `/api/admin/getSFUsersOfOrg?${q.toString()}`,
    );
    return withErrorJson(res);
  }
  /**
   * Assign a Salesforce user as the missed-call owner for one or more queues.
   *
   * Single queue (legacy):
   *   assignQueueOwner({ queueId, sfUserId, searchTenantId? })
   *
   * Normal bulk:
   *   assignQueueOwner({ queues: [{ queueId, queueName? }], sfUserId, searchTenantId? })
   *
   * Select-all across pages:
   *   assignQueueOwner({ selectionMode: "ALL", filters: { search? }, sfUserId, searchTenantId? })
   */
  async assignQueueOwner({ queueId, queues, selectionMode, filters, sfUserId, searchTenantId } = {}) {
    const body = { sfUserId };
    if (queueId)        body.queueId        = queueId;
    if (queues)         body.queues         = queues;
    if (selectionMode)  body.selectionMode  = selectionMode;
    if (filters)        body.filters        = filters;
    if (searchTenantId) body.searchTenantId = searchTenantId;
    const res = await adminAuthenticatedFetch(`/api/admin/queues/assign-owner`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    return withErrorJson(res);
  }
  /**
   * Updates the CLI lookup object selection for a Salesforce org.
   *
   * @param {string}   tenantSfOrgId  - Internal TenantSfOrg UUID
   * @param {string[]} objectIds      - Ordered array e.g. ["Contact","Lead","Account"]
   * @param {string}  [searchTenantId] - Cross-tenant override for super-admins
   */
  async updateCliLookupObjects({ tenantSfOrgId, objectIds, searchTenantId } = {}) {
    const body = { tenantSfOrgId, objectIds };
    if (searchTenantId) body.searchTenantId = searchTenantId;
    const res = await adminAuthenticatedFetch(`/api/admin/sf-orgs/cli-lookup-objects`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
    return withErrorJson(res);
  }

  /**
   * Toggles Matched Record Assignment (is_object_linking_enabled) for a queue.
   *
   * @param {string}  queueId          - The Teams resource-account queue ID
   * @param {boolean} enabled          - true to enable, false to disable
   * @param {string} [searchTenantId]  - Optional; cross-tenant target for super-admins
   */
  async updateMatchedRecordAssignment({ queueId, enabled, searchTenantId } = {}) {
    const body = { queueId, enabled };
    if (searchTenantId) body.searchTenantId = searchTenantId;
    const res = await adminAuthenticatedFetch(`/api/admin/queues/matched-record-assignment`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
    return withErrorJson(res);
  }
}

export default new AdminService();
